import { Form, Select } from 'antd';
import React, { useState, useEffect } from 'react';
import { useGetCategoriesList } from '../../../../utils/api/categories/useGetCategoriesList';
import { useGetMarketplaces } from '../../../../utils/api/marketplaces/useGetMarketplaces';
import { useGetMpCategories } from '../../../../utils/api/mpCategories/useGetMpCategories';
import { useGetStores } from '../../../../utils/api/stores/useGetStores';
import { MP_STATUS_OPTIONS } from '../../../../utils/const/mpProducts';
import { PRODUCT_FILTERS } from '../../../../utils/const/product';
import { useMpProductsFilters } from '../../../../utils/zustand';

const HAS_MAPPING_OPTIONS = [
  { label: 'Avec mapping', value: true },
  { label: 'Sans mapping', value: false },
];

const MpProductFilters = () => {
  const { filters, setFilters } = useMpProductsFilters();

  const [searchedCategoryInput, setSearchedCategoryInput] = useState(null);
  const [searchedCategory, setSearchedCategory] = useState(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchedCategory(searchedCategoryInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchedCategoryInput]);
  const { categories, isLoading: isLoadingCategories } = useGetCategoriesList({
    search: searchedCategory,
    brand: filters?.brand,
    category_mp: filters?.mp_category,
    marketplace__id: filters?.mp_category__marketplace,
    store__id: filters?.store,
    status: filters?.status,
    mapping: filters?.has_mapping,
    limit: 100,
  });
  const { marketplaces, isLoading: isLoadingMarketplaces } = useGetMarketplaces(
    {
      is_active: true,
      brand: filters?.brand,
      store__id: filters?.store,
      categories: filters?.product__category,
      category_mp: filters?.mp_category,
      status: filters?.status,
      mapping: filters?.has_mapping,
    }
  );

  const [searchedMpCategoryInput, setSearchedMpCategoryInput] = useState(null);
  const [searchedMpCategory, setSearchedMpCategory] = useState(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchedMpCategory(searchedMpCategoryInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchedMpCategoryInput]);
  const { mpCategories, isLoading: isLoadingMpCategories } = useGetMpCategories(
    {
      search: searchedMpCategory,
      brand: filters?.brand,
      marketplace__id: filters?.mp_category__marketplace,
      store__id: filters?.store,
      categories: filters?.product__category,
      status: filters?.status,
      mapping: filters?.has_mapping,
      without_attributes: true,
      limit: 100,
    }
  );

  const { stores, isLoading: isLoadingStores } = useGetStores({
    brand: filters?.brand,
    marketplace__id: filters?.mp_category__marketplace,
    categories: filters?.product__category,
    category_mp: filters?.mp_category,
    status: filters?.status,
    mapping: filters?.has_mapping,
  });

  const handleProductCategoryChange = (value) => {
    setSearchedCategoryInput(null);
    setFilters({ ...filters, product__category: value });
  };

  const handleMarketplaceChange = (value) => {
    // Reset mp_category and store when marketplace changes
    setFilters({
      ...filters,
      mp_category__marketplace: value,
      mp_category: undefined,
      store: undefined,
    });
  };

  const handleMpCategoryChange = (value) => {
    setSearchedMpCategoryInput(null);
    setFilters({ ...filters, mp_category: value });
  };

  const handleStatusChange = (value) => {
    setFilters({ ...filters, status: value });
  };

  const handleHasMappingChange = (value) => {
    setFilters({ ...filters, has_mapping: value });
  };

  const handleBrandChange = (value) => {
    setFilters({ ...filters, brand: value });
  };

  const handleStoreChange = (value) => {
    setFilters({ ...filters, store: value });
  };

  return (
    <Form layout="vertical" style={{ width: '100%' }}>
      <h4>Filtres</h4>

      <Form.Item label="Marketplace" style={{ marginBottom: 12 }}>
        <Select
          allowClear
          size="small"
          placeholder="Sélectionner une marketplace"
          style={{ width: '100%' }}
          loading={isLoadingMarketplaces}
          disabled={isLoadingMarketplaces}
          value={filters?.mp_category__marketplace}
          showSearch={true}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={handleMarketplaceChange}
          options={marketplaces?.map((marketplace) => ({
            label: marketplace.name,
            value: marketplace.id,
          }))}
        />
      </Form.Item>

      <Form.Item label="Store" style={{ marginBottom: 12 }}>
        <Select
          allowClear
          size="small"
          placeholder="Sélectionner un store"
          style={{ width: '100%' }}
          loading={isLoadingStores}
          disabled={isLoadingStores}
          value={filters?.store}
          showSearch={true}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={handleStoreChange}
          options={stores?.map((store) => ({
            label: store.name,
            value: store.id,
          }))}
        />
      </Form.Item>

      <Form.Item label="Catégorie Marketplace" style={{ marginBottom: 12 }}>
        <Select
          allowClear
          size="small"
          placeholder="Sélectionner une catégorie MP"
          style={{ width: '100%' }}
          loading={isLoadingMpCategories}
          value={filters?.mp_category}
          showSearch={true}
          filterOption={false}
          onChange={handleMpCategoryChange}
          searchValue={searchedMpCategoryInput}
          onSearch={setSearchedMpCategoryInput}
          options={mpCategories?.map((category) => ({
            label: `${category.label} (${category.name})`,
            value: category.id,
          }))}
        />
      </Form.Item>

      <Form.Item label="Catégorie Produit" style={{ marginBottom: 12 }}>
        <Select
          allowClear
          size="small"
          placeholder="Sélectionner une catégorie"
          style={{ width: '100%' }}
          loading={isLoadingCategories}
          value={filters?.product__category}
          showSearch={true}
          filterOption={false}
          onChange={handleProductCategoryChange}
          searchValue={searchedCategoryInput}
          onSearch={setSearchedCategoryInput}
          options={categories?.map((category) => ({
            label: category.name,
            value: category.id,
          }))}
        />
      </Form.Item>

      <Form.Item label="Marque" style={{ marginBottom: 12 }}>
        <Select
          allowClear
          size="small"
          placeholder="Sélectionner une marque"
          style={{ width: '100%' }}
          value={filters?.brand}
          showSearch={true}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={handleBrandChange}
          options={PRODUCT_FILTERS.brand.map((brand) => ({
            label: brand.charAt(0).toUpperCase() + brand.slice(1),
            value: brand,
          }))}
        />
      </Form.Item>

      <Form.Item label="Statut" style={{ marginBottom: 12 }}>
        <Select
          allowClear
          size="small"
          placeholder="Sélectionner un statut"
          style={{ width: '100%' }}
          value={filters?.status}
          onChange={handleStatusChange}
          options={MP_STATUS_OPTIONS}
        />
      </Form.Item>

      <Form.Item label="Mapping" style={{ marginBottom: 12 }}>
        <Select
          allowClear
          size="small"
          placeholder="Présence d'un mapping"
          style={{ width: '100%' }}
          value={filters?.has_mapping}
          onChange={handleHasMappingChange}
          options={HAS_MAPPING_OPTIONS}
        />
      </Form.Item>
    </Form>
  );
};

export default MpProductFilters;
